import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useComparison } from '../hooks/useComparison';

function HamburgerIcon({ open }: { open: boolean }) {
  return (
    <svg
      className="w-6 h-6"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      {open ? (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M6 18L18 6M6 6l12 12"
        />
      ) : (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 6h16M4 12h16M4 18h16"
        />
      )}
    </svg>
  );
}

export function Header() {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { count: comparisonCount } = useComparison();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    setMobileMenuOpen(false);
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Close menu on route change
  const handleLinkClick = () => {
    setMobileMenuOpen(false);
  };

  const navLinkClass = (path: string) =>
    `text-sm transition-colors ${
      location.pathname === path
        ? 'text-orange-500'
        : 'text-neutral-400 hover:text-white'
    }`;

  const mobileNavLinkClass = (path: string) =>
    `block py-2 text-base transition-colors ${
      location.pathname === path
        ? 'text-orange-500'
        : 'text-neutral-400 hover:text-white'
    }`;

  return (
    <header className="border-b border-neutral-800 bg-neutral-950/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
        <Link to="/" className="flex items-baseline gap-3 hover:opacity-80 transition-opacity" onClick={closeMobileMenu}>
          <span className="font-mono font-bold text-lg text-orange-500 tracking-tight">
            NFA
          </span>
          <span className="text-xs text-neutral-500 uppercase tracking-wider hidden sm:inline">
            notfuckingabout.com
          </span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          <Link to="/routes" className={navLinkClass('/routes')}>
            Routes
          </Link>
          <Link to="/shakedown" className={navLinkClass('/shakedown')}>
            Shakedown
          </Link>
          <Link to="/how-we-pick" className={navLinkClass('/how-we-pick')}>
            How We Pick
          </Link>
          <Link to="/about" className={navLinkClass('/about')}>
            About
          </Link>
          <Link to="/best-for" className={navLinkClass('/best-for')}>
            Best For
          </Link>
          <Link to="/compare" className={`relative ${navLinkClass('/compare')}`}>
            Compare
            {comparisonCount > 0 && (
              <span className="absolute -top-2 -right-3 w-4 h-4 bg-orange-500 text-black text-xs font-bold rounded-full flex items-center justify-center">
                {comparisonCount}
              </span>
            )}
          </Link>

          {/* Auth links */}
          {!isLoading && (
            <>
              {isAuthenticated ? (
                <>
                  <Link to="/history" className={navLinkClass('/history')}>
                    History
                  </Link>
                  <Link to="/gear" className={navLinkClass('/gear')}>
                    Gear Closet
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="text-neutral-400 hover:text-white text-sm transition-colors"
                    title={user?.email || 'Logout'}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="text-orange-500 hover:text-orange-400 text-sm transition-colors"
                >
                  Sign in
                </Link>
              )}
            </>
          )}
        </nav>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-4 md:hidden">
          {/* Show compare badge on mobile */}
          {comparisonCount > 0 && (
            <Link to="/compare" className="relative text-neutral-400" onClick={closeMobileMenu}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-black text-xs font-bold rounded-full flex items-center justify-center">
                {comparisonCount}
              </span>
            </Link>
          )}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-neutral-400 hover:text-white transition-colors"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            <HamburgerIcon open={mobileMenuOpen} />
          </button>
        </div>
      </div>

      {/* Mobile Navigation Panel */}
      {mobileMenuOpen && (
        <nav className="md:hidden border-t border-neutral-800 bg-neutral-950/95 backdrop-blur-sm">
          <div className="px-4 py-4 space-y-1">
            <Link to="/routes" className={mobileNavLinkClass('/routes')} onClick={handleLinkClick}>
              Routes
            </Link>
            <Link to="/shakedown" className={mobileNavLinkClass('/shakedown')} onClick={handleLinkClick}>
              Shakedown
            </Link>
            <Link to="/compare" className={mobileNavLinkClass('/compare')} onClick={handleLinkClick}>
              Compare {comparisonCount > 0 && `(${comparisonCount})`}
            </Link>
            <Link to="/best-for" className={mobileNavLinkClass('/best-for')} onClick={handleLinkClick}>
              Best For
            </Link>

            <div className="border-t border-neutral-800 my-3 pt-3">
              <Link to="/how-we-pick" className={mobileNavLinkClass('/how-we-pick')} onClick={handleLinkClick}>
                How We Pick
              </Link>
              <Link to="/about" className={mobileNavLinkClass('/about')} onClick={handleLinkClick}>
                About
              </Link>
            </div>

            {/* Auth section */}
            {!isLoading && (
              <div className="border-t border-neutral-800 my-3 pt-3">
                {isAuthenticated ? (
                  <>
                    <Link to="/history" className={mobileNavLinkClass('/history')} onClick={handleLinkClick}>
                      History
                    </Link>
                    <Link to="/gear" className={mobileNavLinkClass('/gear')} onClick={handleLinkClick}>
                      Gear Closet
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="block py-2 text-base text-neutral-400 hover:text-white transition-colors w-full text-left"
                    >
                      Logout {user?.email && <span className="text-neutral-600">({user.email})</span>}
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="block py-2 text-base text-orange-500 hover:text-orange-400 transition-colors"
                    onClick={handleLinkClick}
                  >
                    Sign in
                  </Link>
                )}
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
